/**
 * Timezone-safe local date utilities.
 * Avoids the off-by-one day bugs caused by toISOString() in UTC+ / UTC- timezones.
 */

/**
 * Format a Date object or date string to YYYY-MM-DD using local calendar date parts.
 * @param {Date|string|number} date
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatLocalDate = (date) => {
  if (!date) return "";
  if (typeof date === "string") {
    // If it's already an exact YYYY-MM-DD date string, preserve it directly
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
  }

  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date string (e.g. YYYY-MM-DD) into a local Date object.
 * Avoids UTC midnight interpretation shifts.
 * @param {string|Date} dateStr
 * @returns {Date|null}
 */
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return isNaN(dateStr.getTime()) ? null : dateStr;

  const str = String(dateStr).trim();
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    return new Date(year, month, day);
  }

  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

// Earliest date a payment/maturity picker offers: today, or the day after the latest given date (ML-01/02).
// Enforced in the picker only, so editing an older loan with a past saved date is not blocked.
export const minSelectableDate = (...afterDates) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return afterDates.reduce((earliest, value) => {
    const parsed = parseLocalDate(value);
    if (!parsed) return earliest;
    const dayAfter = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() + 1);
    return dayAfter > earliest ? dayAfter : earliest;
  }, today);
};
